import { useState } from "react";
import { Check, Copy } from "lucide-react";
import s from "./CopyButton.module.css";

export interface CopyButtonProps {
  /** What gets written to the clipboard — never what the button displays. */
  text: string;
  /** Shown before a successful copy. */
  label: string;
  /** Shown for 2s after a successful copy, then the button reverts to `label`. */
  copiedLabel: string;
  /** Fired once per tap, before the clipboard write settles — this is the
      caller's hook for its own analytics event, since the two consumers this
      button has today (OrdersView's order number, BookDrawBlock's lisätiedot
      text) report under different names. */
  onCopy?: () => void;
  className?: string;
}

/**
 * Extracted from StoreScreen/OrdersView's order-number row: a tap writes
 * `text` to the clipboard and swaps the icon/label to a checkmark for 2s,
 * regardless of whether the write actually succeeded.
 *
 * That "report success either way" choice is deliberate, not an oversight:
 * the text this button copies is also on screen and selectable by hand, so a
 * clipboard the browser refuses (no permission, an insecure context) costs
 * the client nothing — showing an error for a convenience would cost more
 * than the convenience is worth.
 */
export function CopyButton({ text, label, copiedLabel, onCopy, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  async function handleClick() {
    onCopy?.();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // See the doc comment above — copying is a convenience, not a promise.
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button type="button" className={className ?? s.copy} onClick={handleClick}>
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      <span>{copied ? copiedLabel : label}</span>
    </button>
  );
}
